import { inject, Injectable } from '@angular/core'
import { HttpService, LoggerService, ServiceType } from '@danangelus/ngx-app-core'
import { User } from '../../models/user.model'
import { GatewayError, SessionError } from '../../models/gateway.model'
import { isServerError, withRetry } from '../utils/retry.utils'

const log = LoggerService.get('SsoService')
/**
 * Service to communicate with the SSO services on the back-end
 *
 * @author Dan Bennett
 */
@Injectable({
    providedIn: 'root'
})
export class SsoService {
    private http = inject(HttpService)

    async sendMfa(): Promise<boolean | GatewayError> {
        try {
            log.debug('sendMfa', 'Generating Validation Code and sending to user email')
            const response = await withRetry(
                () => this.http.get(ServiceType.SSO, 'send-new-code', null, null, false, true),
                { maxRetries: 3, shouldRetry: isServerError, label: 'sendMfa' }
            )
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                log.debug('sendMfa', `Error from API: ${response.error}`)
                return new GatewayError(response.body)
            }
            return response
        } catch (e) {
            log.error(`Error sending mfa email! ${e}`)
            throw new GatewayError({ error: 'INTERNAL_SERVER', message: 'Error sending Mfa email' })
        }
    }

    async enrollSmsMfa(mobileNumber: string, smsCode: string): Promise<boolean | SessionError>  {
        log.debug('enrollSmsMfa', `Enrolling SMS MFA for mobile: ${mobileNumber}`)
        try {
            const response = await this.http.post(ServiceType.SSO, 'enroll-sms-mfa', { mobileNumber, smsCode }, null, null, false, true)
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                log.debug('enrollSmsMfa', `Error from API: ${response.error}`)
                return new SessionError(response.body)
            }
            return response
        } catch (e) {
            log.error(`Error enrolling SMS MFA! ${e}`)
            throw new GatewayError({ error: 'INTERNAL_SERVER', message: 'Error enrolling SMS MFA'})
        }
    }

    /**
     * MFA code validation for email, sms, and totp
     * @param code MFA code to validate
     * @param type MFA type: 'email', 'sms', or 'totp'
     * @param setDefault Optional, for sms/totp, whether to set as default
     */
    async checkMfaCode(code: string, type: 'email' | 'sms' | 'totp', setDefault = false): Promise<User | GatewayError | null> {
        log.debug("checkMfaCode", `Validating MFA code for type [${type}], setDefault: ${setDefault}`)
        try {
            const payload: any = { code: code.trim(), type: type.trim() };
            if (type === 'sms' || type === 'totp') {
                payload.setDefault = setDefault;
            }
            const response = await withRetry(
                () => this.http.post(
                    ServiceType.SSO,
                    'validate-mfa',
                    payload,
                    null,
                    null,
                    false,
                    true
                ),
                { maxRetries: 3, shouldRetry: isServerError, label: 'checkMfaCode' }
            );
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                log.debug("checkMfaCode", `Error from API: ${response.error}`);
                return new GatewayError(response.body);
            }
            return new User(response);
        } catch (e) {
            log.error(`Error when validating MFA code [${code}] for type [${type}]! ${e}`);
            throw new GatewayError({ error: 'INTERNAL_SERVER', message: 'Error verifying code for MFA'});
        }
    }

    async getTotpCode(): Promise<string | GatewayError> {
        log.debug('getTotpCode', 'Getting TOTP Generation String for Enrollment')
        try {
            const response = await this.http.get(ServiceType.SSO, 'enroll-totp-mfa', null, null, false, true)
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                log.debug('getTotpCode', `Error from API: ${response.error}`)
                return new GatewayError(response.body)
            }
            return response
        } catch (e) {
            log.error(`Error getting TOTP Connection String! ${e}`)
            throw new GatewayError(({ error: 'INTERNAL_SERVER', message: 'Error getting TOTP Connection String'}))
        }
    }

    async login(username: string, password: string, invalidateOtherSessions = false): Promise<User | SessionError> {
        try {
            const response = await withRetry(
                () => this.http.post(ServiceType.SSO, 'login', { username, password, invalidateOtherSessions }, null, null, false, true),
                { maxRetries: 3, shouldRetry: isServerError, label: 'login' }
            )
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                return new SessionError(response.body)
            }
            return new User(response)
        } catch (e) {
            log.error(`There was an error attempting to login! ${e}`)
            throw e
        }
    }

    async getActiveSessions(page: number, pageSize: number): Promise<any> {
        log.debug('getActiveSessions', 'Fetching active sessions from SSO Backend')
        try {
            return await this.http.get(ServiceType.SSO, 'get-active-sessions', null, { page: `${page}`, pageSize }, false, true)
        } catch (e) {
            log.error(`Error retrieving active sessions from SSO Backend! ${e}`)
            return []
        }
    }

    async changePassword(newPassword: string, confirmPassword: string): Promise<boolean | GatewayError> {
        try {
            const response = await this.http.post(ServiceType.SSO, "change-password", { newPassword, confirmPassword }, null, null, false, true)
            if (Object.prototype.hasOwnProperty.call(response, 'status')) {
                return new GatewayError(response.body)
            }
            return true
        } catch (e) {
            log.error(`Error changing password ${e}`)
            return new GatewayError({ error: 'INTERNAL_SERVER', message: 'Error changing password' })
        }
    }

    async toggleDCS(isDCSLocked: boolean) {
        log.info('[toggleDCS]','Updating backend DCS information')
        try {
            await this.http.post(ServiceType.SSO, 'update-dcs', isDCSLocked.toString(), null, null, false, true)
        } catch (err: any) {
            log.error(`There was an error attempting to update DCS backend flag ${err}`)
        }

    }

    async getDcsStatus(): Promise<boolean> {
        log.info('[getDcsStatus]','Updating backend DCS information')
        try {
            return await this.http.get(ServiceType.SSO, 'dcs-status', null, null, false, true)
        } catch (err: any) {
            log.error(`There was an error attempting to fetch DCS backend flag ${err}`)
            return false
        }
    }

    async deactivateSession(sessionId: string): Promise<boolean> {
        log.info('[deactivateSession]', `Deactivating session with User ID [${sessionId}]`)
        try {
            return await this.http.get(ServiceType.SSO, 'deactivate-session', {sessionId}, null, false, true)
        } catch (err: any) {
            log.error(`There was an error attempting to deactivate user session ${err}`)
            return false
        }
    }
}
